
export default function Terms(){
  return (
    <div className="container py-12 prose max-w-4xl">
      <h1>Terms of Service</h1>
      <p className="text-sm text-gray-600">Effective Date: January 1, 2025 | Last Updated: January 1, 2025</p>

      <h2>1. Agreement to Terms</h2>
      <p>
        These Terms of Service ("Terms") constitute a legally binding agreement between AdKarrot LLC
        ("Company," "we," "us," or "our") and you ("Client," "you," or "your") governing your use of
        our digital marketing services. By engaging our services, you acknowledge that you have read,
        understood, and agree to be bound by these Terms.
      </p>
      <p>
        For information about how we collect, use, and protect your personal data, please see our{' '}
        <a href="/legal/privacy">Privacy Policy</a>. For details about cookies and tracking technologies,
        see our <a href="/legal/cookies">Cookie Policy</a>.
      </p>

      <h2>2. Services</h2>
      <h3>2.1 Services Defined</h3>
      <p>AdKarrot LLC provides digital marketing services including but not limited to:</p>
      <ul>
        <li><strong>SEO Services:</strong> Search engine optimization, on-page optimization, technical SEO, link building, and content strategy</li>
        <li><strong>PPC Management:</strong> Google Ads, Facebook Ads, campaign setup, monitoring, and optimization</li>
        <li><strong>Social Media Marketing:</strong> Content creation, community management, and paid social campaigns</li>
        <li><strong>Content Marketing:</strong> Blog posts, whitepapers, email campaigns, and content strategy</li>
        <li><strong>Strategy Calls:</strong> One-time consultations (60-90 minutes) providing strategic guidance</li>
        <li><strong>SEO/PPC Audits:</strong> Comprehensive analysis and actionable recommendations</li>
        <li><strong>Monthly Growth Retainer:</strong> Ongoing monthly marketing services with defined deliverables</li>
      </ul>

      <h3>2.2 No Guaranteed Results</h3>
      <p className="font-bold">
        COMPANY DOES NOT GUARANTEE SPECIFIC RESULTS. Digital marketing outcomes depend on numerous factors
        outside our control, including but not limited to: search engine algorithm changes, market conditions,
        competition, client website quality, product/service appeal, and economic conditions.
      </p>
      <p>We do not guarantee:</p>
      <ul>
        <li>Specific search engine rankings or organic traffic increases</li>
        <li>Advertising ROI or conversion rates</li>
        <li>Social media follower growth or engagement rates</li>
        <li>Revenue increases or specific business outcomes</li>
      </ul>
      <p>We guarantee professional effort, industry best practices, and transparent reporting.</p>

      <h2>3. Payment Terms</h2>
      <h3>3.1 Payment Processing</h3>
      <p>
        All payments are processed securely through Stripe, Inc. By making a payment, you agree to
        Stripe's Terms of Service and Privacy Policy. We do not store credit card information on our
        servers. Stripe is a PCI DSS Level 1 certified payment processor and handles all payment card
        data securely.
      </p>

      <h3>3.2 Service-Specific Payment Terms</h3>
      <p><strong>Strategy Calls:</strong></p>
      <ul>
        <li>Payment due in full at booking</li>
        <li>Non-refundable after session begins (see Refund Policy for cancellation terms)</li>
      </ul>

      <p><strong>SEO/PPC Audits:</strong></p>
      <ul>
        <li>50% deposit required to commence work</li>
        <li>Remaining 50% due upon delivery of audit report</li>
      </ul>

      <p><strong>Monthly Growth Retainer:</strong></p>
      <ul>
        <li>Billed monthly in advance on subscription start date</li>
        <li>Automatic renewal unless cancelled with 30 days written notice</li>
        <li>Failed payments result in service suspension after 5 business days</li>
        <li>Client responsible for maintaining valid payment method on file</li>
      </ul>

      <h3>3.3 Late Payments</h3>
      <p>
        Services may be suspended if payment is 5 or more days overdue. A $50 late fee applies to
        accounts 10+ days overdue. Accounts 30+ days overdue may be sent to collections, and Client
        will be responsible for all collection costs and attorney fees.
      </p>

      <h3>3.4 Price Changes</h3>
      <p>
        One-time service prices are fixed at booking. Monthly retainer prices may be adjusted with
        30 days written notice. Client may cancel within 30 days of price increase notice without penalty.
      </p>

      <h2>4. Client Responsibilities</h2>
      <h3>4.1 Access and Materials</h3>
      <p>Client agrees to provide:</p>
      <ul>
        <li>Timely access to websites, analytics platforms, advertising accounts, and social media</li>
        <li>Accurate business information, brand guidelines, and target audience data</li>
        <li>Necessary login credentials and permissions within 5 business days of request</li>
        <li>Timely feedback and approvals (within 5 business days unless otherwise specified)</li>
      </ul>

      <h3>4.2 Content Approval</h3>
      <p>
        Client must review and approve deliverables within 5 business days. Failure to respond
        constitutes acceptance. Revisions are limited to 2 rounds per deliverable unless additional
        fees are paid.
      </p>

      <h3>4.3 Legal Compliance</h3>
      <p>Client warrants that:</p>
      <ul>
        <li>All provided content is legal, accurate, and does not infringe third-party rights</li>
        <li>Client is responsible for compliance with FTC disclosure requirements</li>
        <li>Client will not request services that violate platform terms of service (Google, Facebook, etc.)</li>
      </ul>

      <h2>5. Intellectual Property</h2>
      <h3>5.1 Client Materials</h3>
      <p>
        Client retains ownership of all pre-existing materials, logos, trademarks, and intellectual
        property. Client grants Company a non-exclusive license to use such materials solely for
        providing Services.
      </p>

      <h3>5.2 Work Product Ownership</h3>
      <p>Upon full payment, the following transfers to Client:</p>
      <ul>
        <li>Custom content (blog posts, ad copy, graphics created specifically for Client)</li>
        <li>Strategic documents and recommendations</li>
        <li>Campaign data and performance reports</li>
      </ul>

      <p>Company retains ownership of:</p>
      <ul>
        <li>Proprietary methodologies, processes, tools, and techniques</li>
        <li>Pre-existing templates and frameworks</li>
        <li>The right to use de-identified performance data for case studies (with Client's written consent)</li>
      </ul>

      <h3>5.3 Portfolio Rights</h3>
      <p>
        Company may showcase work in its portfolio and marketing materials after project completion,
        including Client name and general results, unless Client opts out in writing to support@adkarrot.com.
      </p>

      <h2>6. Limitation of Liability</h2>
      <h3>6.1 Maximum Liability</h3>
      <p className="font-bold">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMPANY'S TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM
        THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE AMOUNT PAID BY CLIENT IN THE 3 MONTHS PRECEDING
        THE CLAIM, OR $1,000, WHICHEVER IS GREATER.
      </p>

      <h3>6.2 Excluded Damages</h3>
      <p className="font-bold">
        COMPANY SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
        INCLUDING BUT NOT LIMITED TO:
      </p>
      <ul>
        <li>Lost profits, revenue, data, or business opportunities</li>
        <li>Algorithm changes by Google, Facebook, or other platforms</li>
        <li>Account suspensions or bans by third-party platforms</li>
        <li>Negative reviews, reputation damage, or competitive actions</li>
        <li>Delays caused by Client's failure to provide timely materials or approvals</li>
        <li>Third-party platform outages or policy changes</li>
      </ul>

      <h2>7. Indemnification</h2>
      <p>
        Client agrees to indemnify, defend, and hold harmless Company, its officers, employees, and
        contractors from any claims, damages, losses, or expenses (including attorney fees) arising from:
      </p>
      <ul>
        <li>Content, materials, or instructions provided by Client</li>
        <li>Client's products, services, or business practices</li>
        <li>Violation of third-party rights (copyright, trademark, privacy)</li>
        <li>Breach of these Terms by Client</li>
        <li>FTC violations or false advertising claims related to Client-provided content</li>
      </ul>

      <h2>8. Term and Termination</h2>
      <h3>8.1 Service Terms</h3>
      <ul>
        <li>One-time services: Term ends upon delivery and final payment</li>
        <li>Monthly Retainer: Month-to-month, continues until cancelled with 30 days written notice</li>
      </ul>

      <h3>8.2 Cancellation</h3>
      <p>See our <a href="/legal/refunds">Refund and Cancellation Policy</a> for detailed cancellation terms.</p>

      <h3>8.3 Termination by Company</h3>
      <p>Company may terminate services immediately if:</p>
      <ul>
        <li>Payment is 30+ days overdue</li>
        <li>Client breaches material terms of this agreement</li>
        <li>Client requests illegal or unethical services</li>
        <li>Client engages in abusive behavior toward Company staff</li>
      </ul>

      <h3>8.4 Effect of Termination</h3>
      <p>Upon termination:</p>
      <ul>
        <li>Client receives all deliverables for paid periods</li>
        <li>Client must pay for work performed through termination date</li>
        <li>Company retains proprietary methodologies and tools</li>
        <li>All licenses granted to Company terminate</li>
        <li>Client retains ownership of advertising accounts and campaign data</li>
      </ul>

      <h2>9. Confidentiality</h2>
      <p>
        Both parties agree to protect confidential business information, including business strategies,
        financial data, customer lists, login credentials, proprietary processes, and performance data.
        This obligation does not apply to information that is publicly available, independently developed,
        or required to be disclosed by law.
      </p>

      <h2>10. Dispute Resolution</h2>
      <h3>10.1 Governing Law</h3>
      <p>
        These Terms shall be governed by the laws of the State of Iowa, without regard to conflicts of
        law principles.
      </p>

      <h3>10.2 Venue</h3>
      <p>
        Any legal action arising from these Terms shall be brought exclusively in the state or federal
        courts located in Polk County, Des Moines, Iowa. Both parties consent to personal jurisdiction
        in Iowa courts.
      </p>

      <h3>10.3 Good Faith Negotiation</h3>
      <p>
        Before pursuing litigation, parties agree to attempt resolution through good faith discussion
        and, if necessary, mediation in Des Moines, Iowa. Mediation costs will be split equally.
      </p>

      <h3>10.4 No Class Actions</h3>
      <p>
        Any disputes must be brought individually, not as class actions or consolidated proceedings.
      </p>

      <h3>10.5 Attorney Fees</h3>
      <p>
        The prevailing party in any legal action is entitled to recover reasonable attorney fees and costs.
      </p>

      <h2>11. General Provisions</h2>
      <h3>11.1 Changes to Terms</h3>
      <p>
        Company may update these Terms with 30 days notice via email. Continued use of services after
        changes constitutes acceptance. Material changes require affirmative consent.
      </p>

      <h3>11.2 Severability</h3>
      <p>
        If any provision is found unenforceable, the remaining provisions continue in full effect.
      </p>

      <h3>11.3 Entire Agreement</h3>
      <p>
        These Terms, together with any signed Service Agreement, constitute the entire agreement and
        supersede all prior discussions or agreements.
      </p>

      <h3>11.4 No Agency</h3>
      <p>
        No partnership, joint venture, employment, or agency relationship is created by these Terms.
      </p>

      <h3>11.5 Force Majeure</h3>
      <p>
        Neither party shall be liable for delays or failure to perform due to circumstances beyond
        reasonable control, including acts of God, natural disasters, pandemics, or government actions.
      </p>

      <h2>12. Contact Information</h2>
      <p>
        <strong>AdKarrot LLC</strong><br/>
        309 Court Avenue Suite 855<br/>
        Des Moines, IA 50309<br/>
        Email: <a href="mailto:support@adkarrot.com">support@adkarrot.com</a><br/>
        Phone: 319-343-6094
      </p>

      <p className="text-sm text-gray-600 mt-8">
        For legal inquiries: <a href="mailto:legal@adkarrot.com">legal@adkarrot.com</a>
      </p>
    </div>
  )
}
